import { useEffect, useState } from "react";
import { FavoriteMetadata } from "../types/NameResult";

const STORAGE_KEY = "favorites_by_project";
const METADATA_STORAGE_KEY = "favorites_metadata";
const DEFAULT_PROJECT = "Default";

export function useFavorites() {
  const [favorites, setFavorites] = useState<Record<string, string[]>>({ DEFAULT_PROJECT: [] });
  const [metadata, setMetadata] = useState<Record<string, FavoriteMetadata>>({});
  const [currentProject, setCurrentProject] = useState(DEFAULT_PROJECT);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const storedMetadata = localStorage.getItem(METADATA_STORAGE_KEY);
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setFavorites(parsed);
        const defaultExists = parsed[DEFAULT_PROJECT];
        setCurrentProject(defaultExists ? DEFAULT_PROJECT : Object.keys(parsed)[0]);
      } catch {
        setFavorites({ [DEFAULT_PROJECT]: [] });
        setCurrentProject(DEFAULT_PROJECT);
      }
    } else {
      setFavorites({ [DEFAULT_PROJECT]: [] });
      setCurrentProject(DEFAULT_PROJECT);
    }

    if (storedMetadata) {
      try {
        const parsedMetadata = JSON.parse(storedMetadata);
        setMetadata(parsedMetadata);
      } catch {
        setMetadata({});
      }
    }
    
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (loaded) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    }
  }, [favorites, loaded]);

  useEffect(() => {
    if (loaded) {
      localStorage.setItem(METADATA_STORAGE_KEY, JSON.stringify(metadata));
    }
  }, [metadata, loaded]);

  const toggleFavorite = (name: string) => {
    setFavorites((prev) => {
      const currentFavorites = prev[currentProject] || [];
      const isRemoving = currentFavorites.includes(name);
      const updated = isRemoving ? currentFavorites.filter((n) => n !== name) : [...currentFavorites, name];

      // If adding, create metadata entry
      if (!isRemoving) {
        setMetadata((prevMeta) => ({
          ...prevMeta,
          [name]: {
            name,
            isShortlisted: false,
            addedAt: Date.now(),
          },
        }));
      }

      return { ...prev, [currentProject]: updated, };
    });
  };

  const toggleShortlist = (name: string) => {
    setMetadata((prev) => {
      if (!prev[name]) return prev;
      return {
        ...prev,
        [name]: {
          ...prev[name],
          isShortlisted: !prev[name].isShortlisted,
        },
      };
    });
  };

  const updateNotes = (name: string, notes: string) => {
    setMetadata((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        name,
        isShortlisted: prev[name]?.isShortlisted || false,
        addedAt: prev[name]?.addedAt || Date.now(),
        notes,
      },
    }));
  };

  const updateTags = (name: string, tags: string[]) => {
    setMetadata((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        name,
        isShortlisted: prev[name]?.isShortlisted || false,
        addedAt: prev[name]?.addedAt || Date.now(),
        tags,
      },
    }));
  };

  const updateVote = (name: string, vote: number) => {
    setMetadata((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        name,
        isShortlisted: prev[name]?.isShortlisted || false,
        addedAt: prev[name]?.addedAt || Date.now(),
        vote,
        votedAt: Date.now(),
      },
    }));
  };

  const getMetadata = (name: string): FavoriteMetadata | undefined => {
    return metadata[name];
  };

  const getShortlisted = (): string[] => {
    return favorites[currentProject]?.filter((name) => metadata[name]?.isShortlisted) || [];
  };

  const isFavorite = (name: string): boolean => {
    return favorites[currentProject]?.includes(name) || false;
  };

  const addProject = (project: string) => {
    setFavorites((prev) => {
      if (prev[project]) return prev;
      const updated = { ...prev, [project]: [] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    setCurrentProject(project);
  };

  const projects = Object.keys(favorites);

  return {
    favorites: favorites[currentProject] || [],
    projects,
    currentProject,
    setCurrentProject,
    addProject,
    toggleFavorite,
    isFavorite,
    toggleShortlist,
    updateNotes,
    updateTags,
    updateVote,
    getMetadata,
    getShortlisted,
  };
}
